import {
  BIG_ONE,
  BIG_ZERO,
  formatBig,
  gcdBig,
  isqrtBig,
} from "@/lib/tool-kit/math/bigint";
import { factorize, MAX_FACTOR_INPUT } from "@/lib/tool-kit/math/primes";

/**
 * Square roots in simplest radical form: √72 = 6√2.
 *
 * Every square factor comes out from under the root. Up to the factoriser's ceiling
 * that is exact; beyond it, only square factors of primes below ten thousand are pulled
 * out, and the result says so (`complete: false`) — still a correct value, just
 * possibly not the simplest form.
 */

export interface SimplifiedRoot {
  /** The whole number in front: the 6 of 6√2. */
  coefficient: bigint;
  /** What stays under the root, square-free: the 2 of 6√2 (1 for a perfect square). */
  radicand: bigint;
  complete: boolean;
}

const PARTIAL_TRIAL_LIMIT = 10_000;

function partialSimplify(n: bigint): SimplifiedRoot {
  let coefficient = BIG_ONE;
  let rest = n;
  for (let p = 2; p <= PARTIAL_TRIAL_LIMIT; p += p === 2 ? 1 : 2) {
    const square = BigInt(p * p);
    while (rest % square === BIG_ZERO) {
      rest /= square;
      coefficient *= BigInt(p);
    }
  }
  const root = isqrtBig(rest);
  if (root * root === rest) {
    return {
      coefficient: coefficient * root,
      radicand: BIG_ONE,
      complete: true,
    };
  }
  return { coefficient, radicand: rest, complete: false };
}

/** √n = coefficient·√radicand for n ≥ 0. */
export function simplifySqrt(n: bigint): SimplifiedRoot {
  if (n <= BIG_ONE) {
    return { coefficient: n, radicand: BIG_ONE, complete: true };
  }
  const root = isqrtBig(n);
  if (root * root === n) {
    return { coefficient: root, radicand: BIG_ONE, complete: true };
  }
  if (n > MAX_FACTOR_INPUT) {
    return partialSimplify(n);
  }
  let coefficient = BIG_ONE;
  let radicand = BIG_ONE;
  for (const { prime, exponent } of factorize(n).factors) {
    coefficient *= prime ** BigInt(Math.floor(exponent / 2));
    if (exponent % 2 === 1) {
      radicand *= prime;
    }
  }
  return { coefficient, radicand, complete: true };
}

/**
 * A number of the form (coefficient·√radicand) / denominator, reduced: the shape of a
 * distance between two points, or of √(N/M).
 */
export interface RadicalFraction {
  coefficient: bigint;
  radicand: bigint;
  denominator: bigint;
  complete: boolean;
}

/** √(num/den) for num ≥ 0, den > 0, rationalised: √(num·den)/den, then reduced. */
export function sqrtOfFraction(num: bigint, den: bigint): RadicalFraction {
  const root = simplifySqrt(num * den);
  const divisor = gcdBig(root.coefficient, den);
  if (divisor === BIG_ZERO) {
    return {
      coefficient: BIG_ZERO,
      radicand: BIG_ONE,
      denominator: BIG_ONE,
      complete: true,
    };
  }
  return {
    coefficient: root.coefficient / divisor,
    radicand: root.radicand,
    denominator: den / divisor,
    complete: root.complete,
  };
}

/** "6√2", "√3", "5" — a whole number times a square root. */
export function formatRootTerm(coefficient: bigint, radicand: bigint): string {
  if (radicand === BIG_ONE) {
    return formatBig(coefficient);
  }
  const front = coefficient === BIG_ONE ? "" : formatBig(coefficient);
  return `${front}√${radicand.toString()}`;
}

/** "3√2/4", "√5", "7/2". */
export function formatRadicalFraction(value: RadicalFraction): string {
  const top = formatRootTerm(value.coefficient, value.radicand);
  return value.denominator === BIG_ONE
    ? top
    : `${top}/${value.denominator.toString()}`;
}
